import { useCallback, useRef } from 'react';

import { gracefullyDecompressJson } from '../../../compression/lazyStringCompression';
import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS, getMaxDataChannelMessageSizeBytes } from '../../../config';
import { logger } from '../../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../../events';
import { useAtomCallbackMemoized } from '../../../hooks/useAtomCallbackMemoized';
import { debounce, gracefullyParseJson } from '../../../utils';
import type { RemoteDataComponentProps } from '../types';
import { dataChannelAppStateAtom } from './atoms';
import { DataChannelMessageType } from './constants';
import type { DataChannelMessage } from './types';
import { getAppStateFingerprint } from './utils';

const sendDebouncedLoggingEvents = debounce(sendLoggingEvents, DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS);

async function gracefullyParseMessage(
  message: string,
): Promise<[DataChannelMessage, null] | [null, Error] | [null, null]> {
  const maxMessageSizeBytes = getMaxDataChannelMessageSizeBytes();
  return maxMessageSizeBytes
    ? await gracefullyDecompressJson<DataChannelMessage>(message)
    : [gracefullyParseJson<DataChannelMessage>(message), null];
}

export function shouldLogMessage(message: DataChannelMessage, fingerprintsBySender: Map<string, string>) {
  if (message.type !== DataChannelMessageType.APP_STATE) return true;

  const senderKey = JSON.stringify([message.sender.entityType, message.sender.entityId]);
  const fingerprint = getAppStateFingerprint(message);
  if (fingerprintsBySender.get(senderKey) === fingerprint) return false;

  fingerprintsBySender.set(senderKey, fingerprint);
  return true;
}

export function useMessageEventHandlerCallback({
  onMessageReceived,
}: Pick<RemoteDataComponentProps, 'onMessageReceived'>) {
  const getDataTrackState = useAtomCallbackMemoized((get) => get(dataChannelAppStateAtom), []);
  const fingerprintsBySender = useRef(new Map<string, string>());

  return useCallback(
    async (message: string) => {
      const [parsedMessage, error] = await gracefullyParseMessage(message);
      if (!parsedMessage) {
        logger.error(
          'Unable to parse received message',
          {
            message,
          },
          ...(error ? [error] : []),
        );
        return null;
      }

      if (shouldLogMessage(parsedMessage, fingerprintsBySender.current)) {
        logger.info('Received data channel message', { messageType: parsedMessage.type });
      }

      const participantState = getDataTrackState();

      sendDebouncedLoggingEvents(
        TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_RECEIVED,
        {
          isRecording: participantState.isRecording,
          recordingState: participantState.recordingState,
          patientConsent: participantState.patient.consent,
          providerConsent: participantState.provider.consent,
        },
        {
          logLevel: 'info',
          message: 'Received a message on the data track (DEBOUNCED)',
        },
      );
      onMessageReceived?.(parsedMessage);
    },
    [onMessageReceived, getDataTrackState],
  );
}
